/**
 * Dates the SmartFi way: "just now", "8hrs ago", "Yesterday", "April 1, 2026".
 *
 * A full timestamp on every row is noise — nobody reads "Aug 16, 2026, 11:07 PM"
 * to work out that something happened twenty minutes ago. Recent things get a
 * distance, older things get a date, and the exact time stays in the tooltip for
 * the rare moment somebody needs it.
 */

export function relativeTime(value: string | Date | null | undefined): string {
   if (!value) return '—';
   const then = value instanceof Date ? value : new Date(value);
   if (Number.isNaN(then.getTime())) return '—';

   const seconds = Math.floor((Date.now() - then.getTime()) / 1000);

   // A clock a few seconds fast should not produce "in 3 seconds".
   if (seconds < 45) return 'Just now';
   if (seconds < 3600) return `${Math.round(seconds / 60)}min ago`;
   if (seconds < 86400) {
      const hours = Math.round(seconds / 3600);
      return `${hours}${hours === 1 ? 'hr' : 'hrs'} ago`;
   }

   // "Yesterday" means the calendar day before today, not 24 hours ago — the
   // difference matters at breakfast.
   const startOfToday = new Date();
   startOfToday.setHours(0, 0, 0, 0);
   const daysBack = Math.floor((startOfToday.getTime() - then.getTime()) / 86400000);
   if (daysBack === 0) return 'Yesterday';
   if (daysBack < 6) return `${daysBack + 1} days ago`;

   return then.toLocaleDateString('en-PH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
   });
}

/** The full stamp, for a tooltip beside the friendly one. */
export function exactTime(value: string | Date | null | undefined): string {
   if (!value) return '';
   const d = value instanceof Date ? value : new Date(value);
   if (Number.isNaN(d.getTime())) return '';
   return d.toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}
